<template>
<section class="flights-list-page">

  <div class="flights-search">
    <div class="container">
      <div class="row">
        <HotelSearch :showSearchWidget="false" :showSearchToggle="true" />
      </div>
    </div>
  </div>

  <div class="loading-widget-search" v-if="loading">
    <div class="loader"></div>
    <strong>{{ $t('pleaseWaitAMoment') }}</strong> 
    <span>... {{ $t('whileWeFindTheCheapestHotelRatesForYou') }}</span>
  </div>

  <div class="container">
    <div class="row">
        <div class="page-body">
          <div class="search-list-body">

            <aside class="filter-col">
              <div v-if="!loading && !inlineLoading">
                
                <button class="btn filter-toggle-btn" title="toggle Filters" @click="toggleFilters()">
                  <i class="fal fa-filter"></i> 
                  <span v-if="showFilters">{{ $t('hideHotelFilters') }}</span>
                  <span v-else>{{ $t('showHotelFilters') }}</span>
                </button>
                
                <div class="filter-groups" :class="{ 'show-filters': showFilters }">

                  <div class="filter-group">
                    <h4>{{ $t('searchHotelsByName') }}</h4>
                    <div class="search-filter">
                      <a 
                        v-if="filters.hotelName !== ''" 
                        @click="filters.hotelName = ''" 
                        title="Clear Filter">
                        <i class="fal fa-times-circle"></i>
                      </a>
                      <input type="text" v-model="filters.hotelName" />
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4 class="flex">
                      {{ $t('setYourBudget') }}
                      <span class="value">{{ filters.price.priceFilter | formatCurrency($route.params.currency, 0) }}</span>
                    </h4>
                    <div class="price-filter">
                      <range-slider
                        v-model="filters.price.priceFilter"
                        class="slider"
                        :min="filters.price.min"
                        :max="filters.price.max"
                        step="1"
                      >
                      </range-slider>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4 class="flex">
                      {{ $t('guestRating') }}
                      <span class="value" v-if="filters.guestRating.guestRatingFilter">
                        <span class="number">
                          {{ filters.guestRating.guestRatingFilter }} 
                          <i class="fal fa-plus"></i>  
                        </span>
                      </span>
                      <span class="value" v-else>All</span>
                    </h4>

                    <div class="checkbox-filters">
                      <range-slider
                        v-model="filters.guestRating.guestRatingFilter"
                        class="slider"
                        :min="filters.guestRating.min"
                        :max="filters.guestRating.max"
                        step="1"
                      >
                      </range-slider>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4 class="flex">
                      {{ $t('starRating') }}
                      <span class="value" v-if="filters.starRating.starRatingFilter">

                        <span class="stars">
                          <i v-for="star in parseInt(filters.starRating.starRatingFilter)" :key="star" class="fa fa-star"></i>
                          <i v-if="filters.starRating.starRatingFilter % 1 === 0.5" class="fa fa-star-half-alt"></i>
                        </span>
                        <span class="plus"><i class="fal fa-plus"></i></span>
                      </span>
                      <span class="value" v-else>All</span>
                    </h4>
                    <div class="checkbox-filters">
                      <range-slider
                        v-model="filters.starRating.starRatingFilter"
                        class="slider"
                        :min="filters.starRating.min"
                        :max="filters.starRating.max"
                        :step="0.5"
                      >
                      </range-slider>
                    </div>
                  </div>

                  <div class="filter-group">
                    <h4>{{ $t('amenities') }}</h4>
                    <div class="checkbox-filters">
                      <ul>
                        <li v-for="amenity in filteredAmenities" :key="amenity.id">
                          <label :for="amenity.id">
                            <input type="checkbox" v-model="amenity.checked" :id="amenity.id" />
                            <span class="checkmark"></span>
                            {{amenity.name}}
                          </label>
                        </li>
                      </ul>
                      <p class="view-all-link">
                        <a @click="filters.showMoreAmenities = true" v-if="!filters.showMoreAmenities">{{ $t('allAmenities') }}</a>
                        <a @click="filters.showMoreAmenities = false" v-else>{{ $t('lessAmenities') }}</a>
                      </p> 
                    </div>
                  </div>


                  <a class="clear-link" v-if="hasActiveFilters" @click="resetFilters">
                    <i class="fal fa-times-circle"></i>
                    <span class="text">{{ $t('clearFilters') }}</span>
                  </a>

                </div>
              </div>

              <LoadingSearchFilters v-else />
            </aside>

            <section class="search-list-col">
            
              <div class="listing-filters" v-if="!loading && !inlineLoading && apiHotels && apiHotels['getHotelExpress.Results']">
                <ul>
                  <li :class="apiHotels['getHotelExpress.Results'].results.sort_data.sort_by === 'guest_score' ? 'active' : ''">
                    <a @click="sortBy('gs')"><i class="fal fa-smile"></i> {{ $t('guestScore') }}</a>
                  </li>
                  <li :class="apiHotels['getHotelExpress.Results'].results.sort_data.sort_by === 'lowest_price' ? 'active' : ''">
                    <a @click="sortBy('lp')"><i class="fal fa-money-bill-wave"></i> {{ $t('lowestPrice') }}</a>
                  </li>
                  <li :class="apiHotels['getHotelExpress.Results'].results.sort_data.sort_by === 'star_rating' ? 'active' : ''">
                    <a @click="sortBy('sr')"><i class="fal fa-star"></i> {{ $t('starRating') }}</a>
                  </li>
                </ul>
              </div>

              <div v-if="!noResults && apiHotels && apiHotels['getHotelExpress.Results'] && !loading && !inlineLoading">

                <HotelListing 
                    v-for="(hotel, hotelIndex) in offersList.slice(0, itemsToShow)"
                    :key="hotelIndex" 
                    :hotelData="hotel" 
                    :rateData="hotel.room_data[0].rate_data[0].benchmark_price_details"
                />

                <button class="load-more" v-if="offersList.length > itemsToShow" @click="itemsToShow += 10">{{ $t('loadMore') }}</button>
              </div>

              <span v-if="loading || inlineLoading">
                <LoadingHotelListing v-for="(n, index) in 5" :key="index" />
              </span>
              

              <div v-if="noResults" class="no-results">
                <h3>{{ $t('noHotelsFound') }}</h3>
                <p>{{ $t('removeFiltersParagraph') }}</p>
                <p><a @click="resetFilters()" :title="$t('clearFilters')">{{ $t('clearFilters') }}</a></p>
              </div>
            </section>
           
          </div>
        </div>
    </div>
  </div>
</section>
</template>

<script>

import HotelSearchWidget from '@/components/hotels/HotelSearchWidget'
import HotelListing from '@/components/hotels/HotelListing'
import LoadingHotelListing from '@/components/hotels/LoadingHotelListing'
import LoadingSearchFilters from '@/components/LoadingSearchFilters'
import RangeSlider from 'vue-range-slider'
import 'vue-range-slider/dist/vue-range-slider.css'
import HotelsApi from "../../services/hotelsApi";

import { mapGetters } from "vuex";
import { analytics } from "../../firebaseConfig";


export default {
  name: 'HotelsList',
  data () {
    return {
      api: new HotelsApi(),
      suggestion: {},
      hotelResults: {},
      apiHotels: {},
      noResults: false,
      loading: false,
      inlineLoading: false,
      title: 'Hotels from..',
      itemsToShow: 20,
      showFilters: false,
      filters: {
        hotelName: '',
        price: {
          min: 0,
          max: 0,
          priceFilter: 0
        },
        guestRating: {
          guestRatingFilter: 0,
          min: 0,
          max: 9
        },
        starRating: {
          starRatingFilter: 0,
          min: 0,
          max: 4.5
        },
        amenities: [],
        selectedAmenities: [],
        showMoreAmenities: false,
      }
    }
  },
  metaInfo() {
    let self = this;
    return {
      title: self.$t('hotelSearchResults'),
    }
  },

  components: {
    'HotelSearch': HotelSearchWidget,
    HotelListing,
    LoadingHotelListing,
    LoadingSearchFilters,
    RangeSlider,
  },
  watch: {
    '$route': function() {
      this.fetchData(false);
    },

    'filters.amenities': {
      handler: function (newFilters) {
        let result = [];
        for(let i in newFilters) {
          if (newFilters[i].checked) {
            result.push(newFilters[i].id);
          }
        }
        if (JSON.stringify(this.filters.selectedAmenities) !== JSON.stringify(result)) {
          this.filters.selectedAmenities = result;
        }
      },
      deep: true
    }
  },
  methods: {
    sortBy(sort) {
      this.$route.query.sort = sort;
      this.fetchData(false);
    },
    resetFilters() {
      this.filters.hotelName = '';
      this.filters.starRating.starRatingFilter = 0;
      this.filters.guestRating.guestRatingFilter = 0;
      this.filters.price.priceFilter = this.filters.price.max;
      for (let i = 0; i < this.filters.amenities.length; i++) {
        this.filters.amenities[i].checked = false;
      }
    },

    toggleFilters() {
      this.showFilters = !this.showFilters;
    },

    fetchData(isFresh) {
      //debugger;
      let self = this;
      let params = this.$route.params;
      let query = this.$route.query;

      let key = `${params.currency}|${params.destination}|${params.checkIn}|${params.checkOut}|${query.rooms}|${query.adults}|${query.children}|${query.sort}`;

      self.departureToReturnDays = params.checkOut ? self.$moment(params.checkOut).diff(self.$moment(params.checkIn), 'days') + 1: 0;

      let existingResults = self.getHotelResults(key);

      self.noResults = false;

      if (existingResults && (existingResults.timestamp.getTime() + 600000) > new Date().getTime()) {
        self.setupData(existingResults);
      }
      else {
        if (isFresh) {
          self.loading = true;
        } else {
          self.inlineLoading = true;
        }

        self.api.search(
          (
                  params.destinationType.toLowerCase() !== 'hotel'
                  && params.destinationType.toLowerCase() !== 'airport'
                          ? params.destination
                          : ''
          ),
          (
                  params.destinationType === 'hotel'
                          ? params.destination
                          : ''
          ),
          (
                  params.destinationType === 'airport'
                          ? params.destination
                          : ''
          ),
          params.checkIn,
          params.checkOut,
          query.rooms,
          query.adults,
          query.children,
          params.currency,
          query.sort,
          200)
          .then((result) => {
            self.setupData(result.data);
            self.$store.dispatch('saveHotelResults', {key: key, result: result.data});
          })
          .catch((err) => {
            self.loading = false;
            self.inlineLoading = false;
            self.noResults = true;
            self.error = err;
          });
      }

      analytics.logEvent('search', {
        agency: window.TravelPlatform.key,
        type: 'hotel',
        destination: params.destination,
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        adults: query.adults,
        children: query.children,
        currency: params.currency
      })

    },
    setupData(result) {
      let self = this;
      self.apiHotels = result;
      self.apiHotels.timestamp = new Date();
      self.loading = false;
      self.itemsToShow = 20;
      self.inlineLoading = false;
      let results = this.apiHotels['getHotelExpress.Results'].results.hotel_data;
      let prices = results.map((offer) => {
        return offer.room_data[0].rate_data[0].price_details.display_price
      });

      let maxPrice = Math.ceil(Math.max(...prices));
      self.filters.price = {
        min: Math.floor(Math.min(...prices)),
        max: maxPrice,
        priceFilter: maxPrice
      }

      let amenities = {};
      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i].amenity_data.length; j++) {
          let amenity = results[i].amenity_data[j];
          if (parseInt(amenity.id) <= 50) {
            amenities[amenity.id] = {...amenity, checked: false};
          }
        }
      }

      self.filters.amenities = Object.values(amenities);
    },

  },
  mounted () {
    this.api = new HotelsApi();
    this.fetchData(true);
  },
  computed: {
    ...mapGetters ([
      'getCurrencyCode',
      'getHotelResults'
    ]),
    filteredAmenities: function () {
      return this.filters.amenities.slice(0, (this.filters.showMoreAmenities ? this.filters.amenities.length : 5));
    },
    offersList: function () {
      let results = this.apiHotels['getHotelExpress.Results'].results;
      if (results && results.hotel_data) {
        let nameFilter = this.filters.hotelName;
        let priceFilter = this.filters.price.priceFilter;
        let guestRatingFilter = this.filters.guestRating.guestRatingFilter;
        let starRatingFilter = this.filters.starRating.starRatingFilter;
        let selectedAmenities = this.filters.selectedAmenities;
        return results.hotel_data.filter((offer) => {
          return (offer.name.toLowerCase().includes(nameFilter.toLowerCase()))
                  && (offer.room_data[0].rate_data[0].price_details.display_price < priceFilter)
                  && (offer.star_rating >= starRatingFilter)
                  && (offer.review_rating >= guestRatingFilter)
                  && (selectedAmenities.every(id => offer.amenity_data.reduce((acc, amenity)=>{
                    return acc.concat(amenity.id);
                  }, []).includes(id)));
        });
      }
      return [];
    },

    hasActiveFilters() {
      let activeFilters = false;
      if(this.filters.hotelName !== '' || this.filters.starRating.starRatingFilter !== 0 || this.filters.guestRating.guestRatingFilter !== 0 || this.filters.price.priceFilter !== this.filters.price.max) {
        activeFilters = true;
      }
      for (let i = 0; i < this.filters.amenities.length; i++) {
        if(this.filters.amenities[i].checked !== false) {
          activeFilters = true;
        }
      }
      return activeFilters;
    }
  }
}
</script>
